import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { IEmployee } from '@/types/employer';
import { UserRole } from '@/types/user';
import { Delete, Edit, ExpandMore } from '@mui/icons-material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  Button,
  ButtonGroup,
  Chip,
  Link,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { format, isValid } from 'date-fns';
import { MRT_ColumnDef } from 'material-react-table';
import { useState } from 'react';

type GetListEmployeesTableColumnsOpts = {
  onManageLearningBudgetFunds?: (employee: IEmployee) => void;
  onDeleteEmployee?: (employee: IEmployee) => void;
};

export const getListEmployeesTableColumns = ({
  onManageLearningBudgetFunds,
  onDeleteEmployee,
}: GetListEmployeesTableColumnsOpts): MRT_ColumnDef<IEmployee>[] => [
  {
    header: 'Name',
    accessorKey: 'lastName',
    Cell: ({ row: { original } }) => {
      const isEmployerAdmin = original.roles.includes(UserRole.Employer);

      return (
        <Stack>
          <div className="flex items-center gap-2">
            <Typography
              variant="body2"
              className="font-semibold"
              color="text.secondary"
            >
              {[original.firstName, original.lastName]
                .filter(Boolean)
                .join(' ')}
            </Typography>
            {isEmployerAdmin && (
              <Chip
                size="small"
                color="info"
                label="Admin"
                classes={{
                  root: 'p-0 py-0',
                  label: 'text-xs font-semibold',
                }}
              />
            )}
          </div>
          <Typography variant="body2">{original.email}</Typography>
        </Stack>
      );
    },
  },
  {
    header: 'Job Role',
    accessorKey: 'jobRole',
  },
  {
    header: 'Team',
    accessorKey: 'team.name',
  },
  {
    header: 'Learning Budget',
    accessorKey: 'profile.learningBudgetBalance',
    Cell: ({ row: { original } }) => {
      const learningBudgetAmount = original.profile?.learningBudgetBalance ?? 0;

      return (
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={'2px'}
          color={learningBudgetAmount ? 'text.primary' : 'text.secondary'}
        >
          <span className="text-sm">
            <AccountBalanceWalletOutlinedIcon
              fontSize="inherit"
              color="inherit"
            />
          </span>
          <Typography className="font-semibold" color="inherit">
            {formatCurrencyValue(
              original.profile?.learningBudgetBalance ?? 0,
              'eur',
            )}
          </Typography>
        </Stack>
      );
    },
  },
  {
    id: 'lastLogin',
    header: 'Last login',
    accessorFn: ({ lastLogin }) =>
      lastLogin && isValid(new Date(lastLogin))
        ? format(lastLogin, 'PPpp')
        : '-',
  },
  {
    id: 'actions',
    header: 'Actions',
    muiTableHeadCellProps: {
      align: 'right',
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
    grow: 0,
    Cell: ({ row: { original: employee } }) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
      const isOpen = Boolean(anchorEl);

      const showMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
      };

      const hideMenu = () => {
        setAnchorEl(null);
      };

      return (
        <>
          <ButtonGroup
            disableElevation
            variant="outlined"
            size="small"
            aria-label="employee actions"
          >
            <Button
              href={`/employees/${employee._id}`}
              variant="outlined"
              sx={{
                backgroundColor: 'white',
              }}
              className="rounded-r-none border-lines"
            >
              View
            </Button>
            <Button
              size="small"
              onClick={showMenu}
              aria-controls={isOpen ? 'employee-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isOpen ? 'true' : undefined}
              className="rounded-l-none bg-gray-background border-dividers"
            >
              <ExpandMore />
            </Button>
          </ButtonGroup>
          <Menu
            id={`employee-actions-menu-${employee._id}`}
            anchorEl={anchorEl}
            open={isOpen}
            onClose={hideMenu}
            slotProps={{
              list: {
                'aria-labelledby': 'course-actions-button',
              },
            }}
          >
            <MenuItem
              className="border-b last-of-type:border-none"
              href={`/employees/${employee._id}?edit=true`}
            >
              <Link
                href={`/employees/${employee._id}?edit=true`}
                className="no-underline"
              >
                <div className="flex gap-2">
                  <span className="text-sm">
                    <Edit fontSize="inherit" color="inherit" />
                  </span>
                  Edit employee
                </div>
              </Link>
            </MenuItem>
            <MenuItem
              className="border-b last-of-type:border-none"
              onClick={() => {
                onManageLearningBudgetFunds?.(employee);
                hideMenu();
              }}
            >
              <div className="flex gap-2">
                <span className="text-sm">
                  <AccountBalanceWalletOutlinedIcon
                    fontSize="inherit"
                    color="inherit"
                  />
                </span>
                Manage LB funds
              </div>
            </MenuItem>
            <MenuItem
              disabled
              className="border-b last-of-type:border-none"
              onClick={() => {
                onDeleteEmployee?.(employee);
                hideMenu();
              }}
            >
              <div className="flex gap-2">
                <span className="text-sm">
                  <Delete fontSize="inherit" color="inherit" />
                </span>
                Delete
              </div>
            </MenuItem>
          </Menu>
        </>
      );
    },
  },
];
